/* The SouqAkar logo.
 *
 * Ruling (i-2), final 2026-09-25: **the mark is the original logo, used
 * unaltered.** Not redrawn, not traced, not simplified. Bassem's file is the
 * mark, and everything the site shows is that file — keyed to transparency,
 * trimmed of its empty margin, and scaled uniformly. The trace and the three
 * rejected simplified cuts are archived at
 * docs/brand/history/brand2-explorations/.
 *
 * WHY THIS IS AN IMAGE AND NOT AN INLINE SVG. The logo is a raster. Inlining
 * it would mean a base64 blob in every page's JavaScript; as one file it is
 * fetched once, cached, and shared with the favicon, the app icon and the
 * OG card — and it is provably the same bytes as the canonical asset, which
 * an inlined copy would not be.
 *
 * THE LOGO CARRIES ITS OWN WORDMARK, so nothing beside it should repeat the
 * name. It is also part navy, which is why it only ever sits on a LIGHT
 * surface — white or cream. There is no dark variant and there cannot be one
 * without altering the logo: on navy the towers and the wordmark disappear.
 * docs/shots/brand2/FINAL-logo-set.png shows exactly that.
 *
 * `width` is the control, not `size`: the logo is wider than it is tall
 * (aspect 1.232), and height follows from it so the aspect never changes.
 */

export type BrandMarkProps = {
  /** Rendered width in px. Height follows the logo's own aspect ratio. */
  width?: number;
  /** Accessible name. The logo already contains the wordmark, so this is
   *  usually the only place the name needs saying to a screen reader. */
  title?: string;
  className?: string;
};

/** The logo's own aspect, measured from the trimmed source by icons.mjs. */
export const LOGO_ASPECT = 999 / 811;

export function BrandMark({ width = 120, title, className }: BrandMarkProps) {
  return (
    <img
      src="/brand/souqakar-logo.png"
      /* Intrinsic size, so the browser reserves the right box before the file
         arrives and the header does not jump. CSS may scale it down; the
         aspect is fixed either way. */
      width={Math.round(width)}
      height={Math.round(width / LOGO_ASPECT)}
      className={className}
      alt={title ?? ""}
      role={title ? undefined : "presentation"}
      decoding="async"
      /* Eager: it is in the header, above the fold, on every page. Lazy would
         trade a byte saved for a logo that pops in after first paint. */
      loading="eager"
    />
  );
}
